import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { SelectItem } from '@mydocma/dynamic-form/lib/fields/filter-select/select-item';
import { MmViewService } from '../../mm-view.service';
import { MmDbService } from '../../mm-db/mm-db-service';
import { MmProjectDbCollections } from '../../mm-db/mm-project-db-schema';
import { Status } from '@docma-mm-core/status/status';
import { TranslateService } from '@ngx-translate/core';
import { CategoryEnum } from '@docma-mm-core/status/status';
import { EMPTY_FILTER_ID } from './field-mapping.enum';
import { Logger } from '@mydocma/common/src/lib/logger';

@Injectable()
export class FieldsStatusGroupFilterService implements OnDestroy {

  private currentProjectId: number;
  private scStatusGroupsSubject = new BehaviorSubject<SelectItem[]>([]);
  private ctStatusGroupsSubject = new BehaviorSubject<SelectItem[]>([]);
  private destroy$ = new Subject<void>();

  public scStatusGroups$ = this.scStatusGroupsSubject.asObservable();
  public ctStatusGroups$ = this.ctStatusGroupsSubject.asObservable();

  constructor(
    private readonly mmViewService: MmViewService,
    private readonly mmDbService: MmDbService,
    private readonly translateService: TranslateService,
    private readonly logger: Logger
  ) {
    this.initialize();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initialize(): void {
    this.mmViewService.currentProject$
      .pipe(
        filter(project => !!project),
        takeUntil(this.destroy$)
      )
      .subscribe(project => {
        this.currentProjectId = project.Id;
        this.loadStatusGroups(project.MyDocmaUid);
      });
  }

  private async loadStatusGroups(projectMyDocmaUid: string): Promise<void> {
    try {
      const db = await this.mmDbService.getProjectDb(projectMyDocmaUid);
      const statuses = await db.getAll(MmProjectDbCollections.statuses as never) as Status[];

      const scCategories = this.extractStatusCategories(statuses, 'SC');
      const ctCategories = this.extractStatusCategories(statuses, 'CT');

      const scSelectItems = this.formatStatusGroupsForDropdown(scCategories);
      const ctSelectItems = this.formatStatusGroupsForDropdown(ctCategories);

      this.scStatusGroupsSubject.next(scSelectItems);
      this.ctStatusGroupsSubject.next(ctSelectItems);
    } catch (error) {
      this.logger.error(error, '', 'FieldsStatusGroupFilterService');
      this.scStatusGroupsSubject.next(this.getEmptyStatusGroups());
      this.ctStatusGroupsSubject.next(this.getEmptyStatusGroups());
    }
  }

  private extractStatusCategories(statuses: Status[], perspective: 'SC' | 'CT'): Set<number> {
    const categories = new Set<number>();
    const categoryProperty = perspective === 'SC' ? 'CategorySC' : 'CategoryCT';

    statuses.forEach(status => {
      const categoryValue = status[categoryProperty];
      if (categoryValue !== undefined && categoryValue !== null) {
        categories.add(categoryValue);
      }
    });

    return categories;
  }

  private formatStatusGroupsForDropdown(categories: Set<number>): SelectItem[] {
    const selectItems: SelectItem[] = [];
    const sortedCategories = Array.from(categories).sort((a, b) => a - b);

    sortedCategories.forEach(categoryId => {
      const displayText = this.getCategoryDisplayText(categoryId);
      selectItems.push({
        id: categoryId.toString(),
        text: displayText
      });
    });

    return selectItems;
  }

  private getCategoryDisplayText(categoryId: number): string {
    switch (categoryId) {
      case CategoryEnum.Open:
        return this.translateService.instant('MM.fields.statusGroup.open');
      case CategoryEnum.Closed:
        return this.translateService.instant('MM.fields.statusGroup.closed');
      default:
        console.warn('Unknown status category ID:', categoryId);
        return `Category ${categoryId}`;
    }
  }

  private getEmptyStatusGroups(): SelectItem[] {
    return [{
      id: EMPTY_FILTER_ID,
      text: this.translateService.instant('MM.fields.filter.empty')
    }];
  }

  public getStatusGroupDisplayNameById(statusGroupId: string): string {
    let statusGroup = this.scStatusGroupsSubject.value.find(g => g.id === statusGroupId);
    if (!statusGroup) {
      statusGroup = this.ctStatusGroupsSubject.value.find(g => g.id === statusGroupId);
    }
    return statusGroup?.text || statusGroupId;
  }

  public getSCStatusGroupDisplayNameById(statusGroupId: string): string {
    const statusGroups = this.scStatusGroupsSubject.value;
    const group = statusGroups.find(g => g.id === statusGroupId);
    return group?.text || statusGroupId;
  }

  public getCTStatusGroupDisplayNameById(statusGroupId: string): string {
    const statusGroups = this.ctStatusGroupsSubject.value;
    const group = statusGroups.find(g => g.id === statusGroupId);
    return group?.text || statusGroupId;
  }
}
